import { createContext, useContext, useEffect, ReactNode } from 'react';
import { Message } from '../types/chat';
import { useChatOperations, Area } from '../hooks/useChatOperations';
import { TasksProvider } from './TasksContext';


interface ChatContextProps {
  homeId: string;
  messages: Message[];
  isLoading: boolean;
  isLoadingMessages: boolean;
  sendMessage: (messageText: string) => Promise<void>;
  refreshMessages: () => Promise<void>;
  areas: Area[];
  completedTaskIds: Set<string>;
  toggleTaskCompletion: (taskId: string) => void;
  getActiveTaskCount: () => number;
  homePrimaryImage: string | null;
  lastUpdated: number;
}

interface ChatProviderProps {
  children: ReactNode;
  homeId: string;
}

// Export the context so it can be imported by the useChat hook
export const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export function ChatProvider({ children, homeId }: ChatProviderProps) {
  const {
    messages,
    isLoading,
    isLoadingMessages,
    areas,
    lastHomeIdRef,
    sendMessage,
    refreshMessages,
    completedTaskIds,
    toggleTaskCompletion,
    getActiveTaskCount,
    homePrimaryImage,
    lastUpdated
  } = useChatOperations(homeId);

  // Load messages when homeId changes
  useEffect(() => {
    if (homeId && homeId !== lastHomeIdRef.current) {
      console.log('ChatProvider initializing for homeId:', homeId);
      refreshMessages().then(() => {
        lastHomeIdRef.current = homeId;
        console.log('Messages refreshed');
      });
    }
  }, [homeId, refreshMessages]);

  return (
    <ChatContext.Provider value={{ 
      homeId,
      messages, 
      isLoading, 
      isLoadingMessages, 
      sendMessage, 
      refreshMessages,
      areas,
      completedTaskIds,
      toggleTaskCompletion,
      getActiveTaskCount,
      homePrimaryImage,
      lastUpdated
    }}>
      <TasksProvider homeId={homeId}>
        {children}
      </TasksProvider>
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};